/**
 * DOCX Report Generation Utility
 * Generates a structured Word document report with shopping list and all answers
 * Downloads as a .docx file
 */

import {
    Document,
    Packer,
    Paragraph,
    TextRun,
    HeadingLevel,
    BorderStyle,
    Table,
    TableRow,
    TableCell,
    WidthType,
    AlignmentType,
    ShadingType,
    convertInchesToTwip,
} from 'docx';
import { ShoppingListItem, ReportItem, Phase } from '../types';

interface ReportOptions {
    phase: Phase;
    phaseLabel: string;
    area: string;
    shoppingList: ShoppingListItem[];
    report: ReportItem[];
    participantOneName?: string;
    participantTwoName?: string;
}

const BRAND_GREEN = '1A6B3A';
const LIGHT_GREEN = 'E8F5EE';
const LIGHT_RED = 'FEE2E2';
const LIGHT_YELLOW = 'FEF3C7';
const LIGHT_GREY = 'F3F4F6';
const BORDER_GREY = 'D1D5DB';

function sectionHeading(text: string): Paragraph {
    return new Paragraph({
        text,
        heading: HeadingLevel.HEADING_2,
        thematicBreak: false,
        spacing: { before: 400, after: 160 },
        border: {
            bottom: { style: BorderStyle.SINGLE, size: 4, color: BRAND_GREEN },
        },
        run: {
            color: BRAND_GREEN,
        },
    });
}

function labelValueParagraph(label: string, value: string): Paragraph {
    return new Paragraph({
        spacing: { after: 60 },
        children: [
            new TextRun({ text: `${label}: `, bold: true, size: 22 }),
            new TextRun({ text: value, size: 22 }),
        ],
    });
}

function answerBadgeRun(value: string): TextRun {
    const map: Record<string, string> = {
        yes: 'YES',
        no: 'NO',
        'add-to-cart': 'ADD TO BAG',
    };
    const colorMap: Record<string, string> = {
        yes: '166534',
        no: '991B1B',
        'add-to-cart': '92400E',
    };
    return new TextRun({
        text: ` ${map[value] ?? value} `,
        bold: true,
        size: 18,
        color: colorMap[value] ?? '374151',
    });
}

function shoppingItemTable(item: ShoppingListItem, index: number): Table {
    const rows: TableRow[] = [];

    // Header row: number + question ID
    rows.push(
        new TableRow({
            children: [
                new TableCell({
                    shading: { type: ShadingType.CLEAR, fill: BRAND_GREEN },
                    width: { size: 100, type: WidthType.PERCENTAGE },
                    columnSpan: 2,
                    children: [
                        new Paragraph({
                            spacing: { before: 80, after: 80 },
                            children: [
                                new TextRun({
                                    text: `${index + 1}.  ${item.questionId}`,
                                    bold: true,
                                    color: 'FFFFFF',
                                    size: 22,
                                }),
                            ],
                        }),
                    ],
                }),
            ],
        })
    );

    // Question
    rows.push(
        new TableRow({
            children: [
                new TableCell({
                    shading: { type: ShadingType.CLEAR, fill: LIGHT_GREY },
                    width: { size: 100, type: WidthType.PERCENTAGE },
                    columnSpan: 2,
                    children: [
                        new Paragraph({
                            spacing: { before: 100, after: 100 },
                            children: [new TextRun({ text: item.question, size: 22 })],
                        }),
                    ],
                }),
            ],
        })
    );

    // How to Address This
    if (item.detailedQuestion) {
        rows.push(
            new TableRow({
                children: [
                    new TableCell({
                        shading: { type: ShadingType.CLEAR, fill: LIGHT_YELLOW },
                        children: [
                            new Paragraph({
                                spacing: { before: 80, after: 40 },
                                children: [new TextRun({ text: 'The Why', bold: true, size: 20 })],
                            }),
                            new Paragraph({
                                spacing: { after: 80 },
                                children: [new TextRun({ text: item.detailedQuestion, size: 20 })],
                            }),
                        ],
                    }),
                    // End Goal alongside
                    ...(item.recommendation
                        ? [
                              new TableCell({
                                  shading: { type: ShadingType.CLEAR, fill: LIGHT_GREEN },
                                  children: [
                                      new Paragraph({
                                          spacing: { before: 80, after: 40 },
                                          children: [new TextRun({ text: 'Best practice', bold: true, size: 20 })],
                                      }),
                                      new Paragraph({
                                          spacing: { after: 80 },
                                          children: [new TextRun({ text: item.recommendation, size: 20 })],
                                      }),
                                  ],
                              }),
                          ]
                        : [
                              new TableCell({
                                  children: [new Paragraph({ children: [] })],
                              }),
                          ]),
                ],
            })
        );
    } else if (item.recommendation) {
        rows.push(
            new TableRow({
                children: [
                    new TableCell({
                        shading: { type: ShadingType.CLEAR, fill: LIGHT_GREEN },
                        width: { size: 100, type: WidthType.PERCENTAGE },
                        columnSpan: 2,
                        children: [
                            new Paragraph({
                                spacing: { before: 80, after: 40 },
                                children: [new TextRun({ text: 'Best practice', bold: true, size: 20 })],
                            }),
                            new Paragraph({
                                spacing: { after: 80 },
                                children: [new TextRun({ text: item.recommendation, size: 20 })],
                            }),
                        ],
                    }),
                ],
            })
        );
    }

    // Related questions
    if (item.stakeholderSuggestions.length > 0) {
        rows.push(
            new TableRow({
                children: [
                    new TableCell({
                        width: { size: 100, type: WidthType.PERCENTAGE },
                        columnSpan: 2,
                        children: [
                            new Paragraph({
                                spacing: { before: 80, after: 40 },
                                children: [new TextRun({ text: 'Related Questions', bold: true, size: 20 })],
                            }),
                            ...item.stakeholderSuggestions.map(
                                (s) =>
                                    new Paragraph({
                                        bullet: { level: 0 },
                                        spacing: { after: 40 },
                                        children: [new TextRun({ text: s, size: 20 })],
                                    })
                            ),
                        ],
                    }),
                ],
            })
        );
    }

    return new Table({
        width: { size: 100, type: WidthType.PERCENTAGE },
        margins: {
            top: convertInchesToTwip(0.04),
            bottom: convertInchesToTwip(0.04),
            left: convertInchesToTwip(0.1),
            right: convertInchesToTwip(0.1),
        },
        borders: {
            top: { style: BorderStyle.SINGLE, size: 4, color: BORDER_GREY },
            bottom: { style: BorderStyle.SINGLE, size: 4, color: BORDER_GREY },
            left: { style: BorderStyle.SINGLE, size: 4, color: BORDER_GREY },
            right: { style: BorderStyle.SINGLE, size: 4, color: BORDER_GREY },
            insideHorizontal: { style: BorderStyle.SINGLE, size: 2, color: BORDER_GREY },
            insideVertical: { style: BorderStyle.SINGLE, size: 2, color: BORDER_GREY },
        },
        rows,
    });
}

function answerRow(item: ReportItem): TableRow {
    const bgMap: Record<string, string> = {
        yes: LIGHT_GREEN,
        no: LIGHT_RED,
        'add-to-cart': LIGHT_YELLOW,
    };
    const fill = bgMap[item.value] ?? 'FFFFFF';

    return new TableRow({
        children: [
            new TableCell({
                width: { size: 12, type: WidthType.PERCENTAGE },
                shading: { type: ShadingType.CLEAR, fill },
                children: [
                    new Paragraph({
                        alignment: AlignmentType.CENTER,
                        spacing: { before: 80, after: 80 },
                        children: [answerBadgeRun(item.value)],
                    }),
                ],
            }),
            new TableCell({
                width: { size: 15, type: WidthType.PERCENTAGE },
                children: [
                    new Paragraph({
                        spacing: { before: 80, after: 80 },
                        children: [new TextRun({ text: item.questionId, size: 18, color: '6B7280', font: 'Courier New' })],
                    }),
                ],
            }),
            new TableCell({
                width: { size: 73, type: WidthType.PERCENTAGE },
                children: [
                    new Paragraph({
                        spacing: { before: 80, after: 80 },
                        children: [new TextRun({ text: item.question, size: 20 })],
                    }),
                ],
            }),
        ],
    });
}

export async function generateASCIIReport({ phase, phaseLabel, area, shoppingList, report, participantOneName, participantTwoName }: ReportOptions): Promise<void> {
    const counts = report.reduce(
        (acc, item) => {
            acc[item.value] = (acc[item.value] || 0) + 1;
            return acc;
        },
        {} as Record<string, number>
    );

    const enablingYes = report.filter((r) => r.value === 'yes' && r.enabling === true);

    const children: (Paragraph | Table)[] = [];

    // ── Title ──
    children.push(
        new Paragraph({
            text: 'Shopping Bag Assessment Report',
            heading: HeadingLevel.HEADING_1,
            alignment: AlignmentType.CENTER,
            spacing: { after: 120 },
            run: { color: BRAND_GREEN },
        })
    );

    // ── Meta ──
    if (participantOneName && participantTwoName) {
        children.push(labelValueParagraph('Participants', `${participantOneName} & ${participantTwoName}`));
    }
    children.push(labelValueParagraph('Phase', phaseLabel));
    children.push(labelValueParagraph('Area', area));
    children.push(
        labelValueParagraph(
            'Generated',
            new Date().toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
            })
        )
    );
    children.push(new Paragraph({ text: '', spacing: { after: 200 } }));

    // ── Summary stats table ──
    children.push(sectionHeading('Summary'));
    children.push(
        new Table({
            width: { size: 100, type: WidthType.PERCENTAGE },
            borders: {
                top: { style: BorderStyle.SINGLE, size: 4, color: BORDER_GREY },
                bottom: { style: BorderStyle.SINGLE, size: 4, color: BORDER_GREY },
                left: { style: BorderStyle.SINGLE, size: 4, color: BORDER_GREY },
                right: { style: BorderStyle.SINGLE, size: 4, color: BORDER_GREY },
                insideVertical: { style: BorderStyle.SINGLE, size: 2, color: BORDER_GREY },
            },
            rows: [
                new TableRow({
                    tableHeader: true,
                    children: ['Total Questions', 'Yes', 'No', 'Added to Bag'].map(
                        (label) =>
                            new TableCell({
                                shading: { type: ShadingType.CLEAR, fill: LIGHT_GREY },
                                children: [
                                    new Paragraph({
                                        alignment: AlignmentType.CENTER,
                                        children: [new TextRun({ text: label, bold: true, size: 20 })],
                                    }),
                                ],
                            })
                    ),
                }),
                new TableRow({
                    children: [
                        String(report.length),
                        String(counts['yes'] || 0),
                        String(counts['no'] || 0),
                        String(counts['add-to-cart'] || 0),
                    ].map(
                        (val) =>
                            new TableCell({
                                children: [
                                    new Paragraph({
                                        alignment: AlignmentType.CENTER,
                                        children: [new TextRun({ text: val, bold: true, size: 28 })],
                                    }),
                                ],
                            })
                    ),
                }),
            ],
        })
    );
    children.push(new Paragraph({ text: '', spacing: { after: 200 } }));

    // ── Enabling cards ──
    if (enablingYes.length > 0) {
        children.push(sectionHeading('Enabling Cards (Yes Answers)'));
        children.push(
            new Paragraph({
                spacing: { after: 120 },
                children: [
                    new TextRun({
                        text: 'You have answered yes to questions that indicates that your VR can contribute to improving the delivery of service or that it has the potential to support the protection of human rights. Use them to support your application',
                        italics: true,
                        size: 21,
                    }),
                ],
            })
        );
        enablingYes.forEach((item) => {
            children.push(
                new Paragraph({
                    bullet: { level: 0 },
                    spacing: { after: 80 },
                    children: [
                        new TextRun({ text: `${item.questionId}  `, bold: true, size: 20, color: '6B7280', font: 'Courier New' }),
                        new TextRun({ text: item.question, size: 20 }),
                    ],
                })
            );
        });
        children.push(new Paragraph({ text: '', spacing: { after: 200 } }));
    }

    // ── Shopping bag items ──
    children.push(sectionHeading('Shopping Bag Items'));

    if (shoppingList.length === 0) {
        children.push(
            new Paragraph({
                spacing: { after: 200 },
                children: [
                    new TextRun({
                        text: 'Your shopping bag is empty — all items were answered with "yes" or "no".',
                        italics: true,
                        size: 21,
                    }),
                ],
            })
        );
    } else {
        shoppingList.forEach((item, index) => {
            children.push(shoppingItemTable(item, index));
            children.push(new Paragraph({ text: '', spacing: { after: 160 } }));
        });
    }

    // ── Action planning workspace ──
    children.push(sectionHeading('Action Plan'));
    children.push(
        new Paragraph({
            spacing: { after: 100 },
            children: [
                new TextRun({
                    text: 'Use the space below to plan how you will address the items in your shopping bag.',
                    italics: true,
                    size: 21,
                    color: '6B7280',
                }),
            ],
        })
    );

    const workspaceHeadings = [
        'How should we proceed? (planning and reasoning)',
        'Final goal',
        'Sub\u2011goals',
        'Responsible person (Who?)',
        'Timeline',
    ];

    for (const heading of workspaceHeadings) {
        children.push(
            new Paragraph({
                text: heading,
                heading: HeadingLevel.HEADING_3,
                spacing: { before: 320, after: 80 },
                run: { bold: true, size: 24 },
            })
        );
        children.push(
            new Paragraph({
                spacing: { after: 60 },
                border: {
                    bottom: { style: BorderStyle.SINGLE, size: 1, color: BORDER_GREY },
                },
                children: [new TextRun({ text: ' ', size: 22 })],
            })
        );
        children.push(
            new Paragraph({
                spacing: { after: 60 },
                border: {
                    bottom: { style: BorderStyle.SINGLE, size: 1, color: BORDER_GREY },
                },
                children: [new TextRun({ text: ' ', size: 22 })],
            })
        );
        children.push(
            new Paragraph({
                spacing: { after: 60 },
                border: {
                    bottom: { style: BorderStyle.SINGLE, size: 1, color: BORDER_GREY },
                },
                children: [new TextRun({ text: ' ', size: 22 })],
            })
        );
        children.push(
            new Paragraph({
                spacing: { after: 60 },
                border: {
                    bottom: { style: BorderStyle.SINGLE, size: 1, color: BORDER_GREY },
                },
                children: [new TextRun({ text: ' ', size: 22 })],
            })
        );
        children.push(new Paragraph({ text: '', spacing: { after: 80 } }));
    }

    children.push(new Paragraph({ text: '', spacing: { after: 200 } }));

    // ── Full answer report ──
    children.push(sectionHeading('Full Answer Report'));
    children.push(
        new Table({
            width: { size: 100, type: WidthType.PERCENTAGE },
            borders: {
                top: { style: BorderStyle.SINGLE, size: 4, color: BORDER_GREY },
                bottom: { style: BorderStyle.SINGLE, size: 4, color: BORDER_GREY },
                left: { style: BorderStyle.SINGLE, size: 4, color: BORDER_GREY },
                right: { style: BorderStyle.SINGLE, size: 4, color: BORDER_GREY },
                insideHorizontal: { style: BorderStyle.SINGLE, size: 2, color: BORDER_GREY },
                insideVertical: { style: BorderStyle.SINGLE, size: 2, color: BORDER_GREY },
            },
            rows: [
                new TableRow({
                    tableHeader: true,
                    children: ['Answer', 'ID', 'Question'].map(
                        (label) =>
                            new TableCell({
                                shading: { type: ShadingType.CLEAR, fill: LIGHT_GREY },
                                children: [
                                    new Paragraph({
                                        children: [new TextRun({ text: label, bold: true, size: 20 })],
                                    }),
                                ],
                            })
                    ),
                }),
                ...report.filter((r) => !(r.value === 'yes' && r.enabling === true)).map(answerRow),
            ],
        })
    );

    const doc = new Document({
        styles: {
            default: {
                document: {
                    run: { font: 'Calibri', size: 22 },
                },
                heading1: {
                    run: { font: 'Calibri', bold: true, size: 36, color: BRAND_GREEN },
                },
                heading2: {
                    run: { font: 'Calibri', bold: true, size: 26, color: BRAND_GREEN },
                },
            },
        },
        sections: [
            {
                properties: {
                    page: {
                        margin: {
                            top: convertInchesToTwip(1),
                            right: convertInchesToTwip(1),
                            bottom: convertInchesToTwip(1),
                            left: convertInchesToTwip(1),
                        },
                    },
                },
                children,
            },
        ],
    });

    const blob = await Packer.toBlob(doc);
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `Shopping-Bag-Report-${phaseLabel.replace(/\s+/g, '-')}-${area.replace(/\s+/g, '-')}-${Date.now()}.docx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
}
